import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo, LevelBadge } from "./Logo";
import { mediaSrc } from "../api";
import {
  LayoutDashboard, BookOpen, Flag, Trophy, Award, Megaphone, User, LogOut, Shield, Flame, Menu, X,
} from "lucide-react";
import { useState } from "react";

const nav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/learning", label: "Learning", icon: BookOpen },
  { to: "/app/ctf", label: "CTF Arena", icon: Flag },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/app/certificates", label: "Certificates", icon: Award },
  { to: "/app/announcements", label: "Announcements", icon: Megaphone },
];

export function LearnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/app/dashboard" data-testid="nav-home-link">
              <Logo className="h-9" withText />
            </Link>
            <nav className="hidden lg:flex items-center gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "text-[#F26522] bg-[#F26522]/10" : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1 text-orange-400" title="Streak">
                <Flame size={16} /> {user?.streak || 0}
              </span>
              <LevelBadge level={user?.level || 1} />
            </div>
            {user?.role === "super_admin" && (
              <Link to="/admin" className="btn-secondary !py-1.5 !px-3 text-sm" data-testid="nav-admin-link">
                <Shield size={16} /> Admin
              </Link>
            )}
            <Link to="/app/profile" className="btn-ghost !px-2" data-testid="nav-profile" title="Profile">
              <div className="h-8 w-8 rounded-full bg-[#1B2A4A] border border-white/10 flex items-center justify-center text-sm font-semibold text-[#38bdf8] overflow-hidden">
                {user?.avatar_url ? <img src={mediaSrc(user.avatar_url)} alt="" className="h-full w-full object-cover" /> : (user?.full_name || "U").charAt(0).toUpperCase()}
              </div>
            </Link>
            <button className="btn-ghost !px-2 hidden sm:flex" onClick={doLogout} data-testid="logout-btn" title="Logout">
              <LogOut size={18} />
            </button>
            <button className="btn-ghost !px-2 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-white/5 px-4 py-3 space-y-1 bg-[#0B1120]">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive ? "text-[#F26522] bg-[#F26522]/10" : "text-slate-300"
                  }`
                }
              >
                <n.icon size={18} /> {n.label}
              </NavLink>
            ))}
            <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 text-red-400 w-full">
              <LogOut size={18} /> Logout
            </button>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
